import { inject } from '@angular/core';
import { CanActivateFn, Params, Router, UrlTree } from '@angular/router';

import { activeClientPersonId } from './client-session';
import { OnboardingResumeTarget, OnboardingStateService } from './onboarding-state.service';
import { PROJECT_URL, ProjectRoleService } from './project-role';
import { VerdictBudget } from './route-wait';
import { PartnerStudioResource } from '../features/partner/domain/infrastructure/partner-cabinet.resource';

/** The shared secret door: any value passes. Handed to a user who must reach the cabinet unfinished. */
export const SKIP_ONBOARDING_PARAM = 'skip-onboarding';

export function buildOnboardingParams(target: OnboardingResumeTarget): Params {
  return {
    step: target.step,
    ...(target.projectId ? { projectId: target.projectId } : {}),
    ...(target.designId ? { design: target.designId } : {}),
    ...(target.mode ? { mode: target.mode } : {}),
    ...(target.resumeSuccess ? { resume: 'success' } : {}),
  };
}

/**
 * The navigation the gate performs to resume a not-onboarded user: the glued `/onboarding` funnel by
 * default, or — for a subsequent album-less project on an already-onboarded account — that project's
 * own standalone «Создать проект» wizard (`projects/new`) resumed at its persisted step. One source of
 * truth for both the route guard and the shell's preview-exit path.
 */
export function resumeNavigation(target: OnboardingResumeTarget): { commands: unknown[]; extras: { queryParams: Params } } {
  if (target.surface === 'projectWizard' && target.projectId) {
    return {
      commands: ['/projects/new'],
      extras: {
        queryParams: {
          projectId: target.projectId,
          step: target.step,
          ...(target.name ? { name: target.name } : {}),
        },
      },
    };
  }

  return { commands: ['/onboarding'], extras: { queryParams: buildOnboardingParams(target) } };
}

/**
 * A produced album grants cabinet entry for good; until then any cabinet URL is redirected into the
 * onboarding wizard, resumed at the furthest reached step. The allow-list is enforced by NOT attaching
 * this guard to those routes; the `/onboarding` prefix check is a defensive stop against a redirect loop.
 * Гвард маршрутизирует, а не защищает: вердикт, не успевший за бюджет навигации, пропускает, а визард
 * открывается поздно — если человек всё ещё на том адресе.
 */
export const onboardingGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const roles = inject(ProjectRoleService);
  const onboarding = inject(OnboardingStateService);
  const studios = inject(PartnerStudioResource);
  const budget = inject(VerdictBudget);

  if (route.queryParamMap.has(SKIP_ONBOARDING_PARAM)) {
    return true;
  }

  if (state.url.split('?')[0].startsWith('/onboarding')) {
    return true;
  }

  // A logged-in student (person session) owns no cabinet projects — onboarding never applies to them.
  if (activeClientPersonId()) {
    return true;
  }

  // Read the organizer role from the NAVIGATING url, not the lagging url() signal: this guard runs
  // before NavigationEnd, so isOrganizerOnly() would still see the previous route and wrongly send a
  // coordinator (whose roles were just seeded) into the onboarding wizard.
  // `projects/new` is the standalone wizard, not a project id — reading it as one would ask the gate
  // about a project that does not exist and redirect the wizard away from itself on a hard reload.
  const matchedSegment = state.url.split(/[?#]/)[0].match(PROJECT_URL)?.[1];
  const targetProjectId = matchedSegment === 'new' ? undefined : matchedSegment;
  if (targetProjectId && roles.isProjectOrganizerOnly(targetProjectId)) {
    return true;
  }

  return budget.decide(onboardingVerdict(router, studios, onboarding, targetProjectId), state.url);
};

async function onboardingVerdict(
  router: Router,
  studios: PartnerStudioResource,
  onboarding: OnboardingStateService,
  targetProjectId: string | undefined,
): Promise<true | UrlTree> {
  // A partner studio sells templates and owns no projects or albums — the wizard it would be sent into
  // asks for a shoot it will never have. The memoized studio read answers once per session.
  if (await studios.myStudio()) {
    return true;
  }

  const resume = await onboarding.resolve(targetProjectId);
  if (resume === null || resume.onboarded) {
    return true;
  }

  const { commands, extras } = resumeNavigation(resume.target);
  return router.createUrlTree(commands, extras);
}
